// (c) 2026 Owl Consultations. All rights reserved.
// D-SUPPORT-1 — user-initiated session recorder ("Record this issue").
// rrweb is loaded on demand (dynamic import) so the main bundle is unaffected.
// FAIL-SOFT BY CONTRACT: any recorder/ingest failure stops recording silently and
// must never break the app the user is trying to show us.
// Masking: all inputs masked by default; elements with class "replay-block" are
// blocked entirely (use on password/SSN/DOB fields as needed beyond input masking).
import * as api from '../api/client'

type StopFn = () => void

interface RecorderState {
  sessionId: string | null
  stopFn: StopFn | null
  events: unknown[]
  timer: ReturnType<typeof setInterval> | null
  startedAt: number
  bytesSent: number
}

const state: RecorderState = { sessionId: null, stopFn: null, events: [], timer: null, startedAt: 0, bytesSent: 0 }
const BATCH_MS = 10_000
const MAX_MINUTES = 15

// Pub/sub so any component (the global widget, the Support button) reflects live state
// regardless of which page is mounted. The recorder itself lives at module scope, so it
// keeps running across route changes — only the UI needs to re-subscribe.
type Listener = () => void
const listeners = new Set<Listener>()
function notify() { listeners.forEach(l => { try { l() } catch { /* ignore */ } }) }
export function subscribeRecording(l: Listener): () => void { listeners.add(l); return () => listeners.delete(l) }
export function recordingStartedAt(): number { return state.startedAt }
export function maxRecordingMinutes(): number { return MAX_MINUTES }

export function isRecording(): boolean { return state.sessionId !== null }

async function gzip(json: string): Promise<ArrayBuffer> {
  const cs = new CompressionStream('gzip')
  const stream = new Blob([json]).stream().pipeThrough(cs)
  return await new Response(stream).arrayBuffer()
}

async function flush(final = false): Promise<void> {
  if (!state.sessionId || state.events.length === 0) return
  const batch = state.events
  state.events = []
  try {
    const payload = await gzip(JSON.stringify(batch))
    state.bytesSent += payload.byteLength
    await api.replayIngest(state.sessionId, payload)
  } catch {
    // Ingest failed (caps hit, offline, etc.) — stop silently, keep the app alive.
    if (!final) { internalStop(); clearSession() }
  }
}

function internalStop(): void {
  if (state.timer) { clearInterval(state.timer); state.timer = null }
  try { state.stopFn?.() } catch { /* recorder already gone */ }
  state.stopFn = null
}

function clearSession(): void { state.sessionId = null; state.startedAt = 0; notify() }

/** Start recording. Returns the replay session id, or null if recording couldn't start. */
export async function startRecording(): Promise<string | null> {
  if (state.sessionId) return state.sessionId
  try {
    const { id } = await api.replayStart()
    const rrweb = await import('rrweb')
    state.sessionId = id
    state.events = []
    state.bytesSent = 0
    state.startedAt = Date.now()
    state.stopFn = rrweb.record({
      emit: (ev: unknown) => { state.events.push(ev) },
      maskAllInputs: true,
      blockClass: 'replay-block',
      sampling: { mousemove: 50, scroll: 150, input: 'last' },
    }) as StopFn
    notify()
    state.timer = setInterval(() => {
      if (Date.now() - state.startedAt > MAX_MINUTES * 60_000) { void stopRecording(null) ; return }
      void flush()
    }, BATCH_MS)
    return id
  } catch {
    internalStop()
    clearSession()
    return null
  }
}

/** Stop + finalize; optionally link to a support ticket. Returns the session id (or null). */
export async function stopRecording(ticketId: string | null, userNote?: string): Promise<string | null> {
  const id = state.sessionId
  if (!id) return null
  internalStop()
  const duration = Date.now() - state.startedAt
  await flush(true)
  clearSession()
  try {
    await api.replayComplete(id, { ticket_id: ticketId ?? undefined, user_note: userNote, duration_ms: duration })
  } catch { /* metadata completion is best-effort; batches are already stored */ }
  return id
}
